import { useRouter } from 'next/navigation'
import { useEffect } from 'react'

export function useUnsavedChanges(isDirty) {
  const _router = useRouter()

  useEffect(() => {
    // 1. Handle browser close / reload / unexpected exit
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = '' // Chrome requires returnValue to be set
      }
    }

    // 2. Handle internal client-side navigation
    // This is the tricky part in App Router. We can't easily intercept "router.push".
    // But we can intercept clicks on links.
    // The robust way is to monkey-patch `pushState` (history changes) BUT Next.js overrides it.

    // Simplest robust solution for Next.js App Router currently:
    // Intercept clicks on links globally or assume the user accepts that "browsing away" strictly inside the app
    // might not trigger it unless we use a custom Link component.
    // However, the user asked specifically for "clicking on another choice in the nav menu".
    // Those are standard Links.

    // Let's try to add a global click listener to intercept <a> clicks.
    const handleAnchorClick = (e) => {
      if (isDirty) {
        const target = e.target.closest('a')
        if (target && target.href) {
          // Check if it's a navigation
          // If it's a download or target=_blank, ignore
          if (target.target === '_blank' || target.download) return

          // Show confirmation
          // Using window.confirm is the only sync way to block
          if (
            !window.confirm(
              'You have unsaved changes. Are you sure you want to leave?',
            )
          ) {
            e.preventDefault()
            e.stopPropagation()
          }
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    // Use capture phase to intercept before Next.js Link handles it
    window.addEventListener('click', handleAnchorClick, true)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('click', handleAnchorClick, true)
    }
  }, [isDirty])
}
